import {
    browserQuery,
    listSpec,
    oneSpec,
    useFreshQuery,
    type Page,
    type QueryParams,
} from '@bemor/web-kit';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * A cursor-paged list, following the freshness contract. The key and the data shape come from the kit's query spec,
 * the same one a route's `handle.prefetch` declares, so a server-rendered page finds its data (BM-46 §2.0).
 */
export function useList<T>(
    path: string,
    params: QueryParams = {},
    enabled = true,
) {
    return useFreshQuery<Page<T>>({
        ...browserQuery<Page<T>>(listSpec(path, params)),
        enabled,
    });
}

/** One resource (the detail pages read the owning module directly). */
export function useOne<T>(path: string | null, params: QueryParams = {}) {
    return useFreshQuery<T>({
        ...browserQuery<T>(oneSpec(path ?? '', params)),
        enabled: path !== null,
    });
}

/** A write that invalidates the given query prefixes once applied. */
export function useWrite<TBody, TResult>(
    run: (body: TBody) => Promise<TResult>,
    invalidate: string[] = [],
) {
    const client = useQueryClient();
    return useMutation({
        mutationFn: run,
        onSuccess: async () => {
            await Promise.all(
                invalidate.map((p) =>
                    client.invalidateQueries({
                        predicate: (q) =>
                            JSON.stringify(q.queryKey).includes(p),
                    }),
                ),
            );
        },
    });
}
